"use client";

import { useEffect, useRef, useState } from "react";
import { fetchData } from "@repo/shared-utils/fetch-utils";
import { HeaderFooterApiEndpoints } from "../../constants/HeaderFooterApiEndpoints";
import { redirectSearchWebForm } from "@repo/shared-utils/common-utils";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/global-store";
import {
  handleSearchBackIconClickDL,
  handleSearchSubmitDL,
  handleSearchSuggestionClickDL,
  handleSearchHistoryDeleteDL,
} from "./SearchAnalyticsFunction";
import {
  SEC_TITLE_BACK_ICON,
  SEC_TITLE_SEARCH,
  SEC_TITLE_SEARCH_HISTORY,
  SEC_TITLE_SEARCH_SUGGESTIONS,
} from "../../constants/HeaderConstants";

const Search = ({
  searchResultUrl,
  version,
  isMyaccountSearchURL,
}: {
  searchResultUrl: string;
  version?: string;
  isMyaccountSearchURL?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [searchData, setSearchData] = useState<{ keywords: string[] } | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isUserLogin = useAppSelector(selectIsAuthSuccessOrPerpetual);

  // TODO(B2B): Keep this URL hardcoded temporarily.
  // We are currently not able to capture the required user event, and AEM-authored URL changes
  // are not reflecting at runtime because the URL is hardcoded at code level.
  // Remove this hardcoded fallback once event capture and AEM-driven URL resolution are fixed.
  const updateUrlParam = () => {
    const url = isMyaccountSearchURL
      ? "https://www.bajajfinserv.in/myaccount/search/content"
      : searchResultUrl || "https://www.bajajfinserv.in/webform/v1/search/content";
    const urlObj = new URL(url);
    return urlObj.href;
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    const stored = safeLocalStorageParse<string[]>("searchKeyword", []);
    setSearchHistory(stored);
    setShowDropdown(true);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchValue.trim()) {
        fetchSearch(searchValue);
      } else {
        setSearchData(null);
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const contentEl = document.querySelector(".content-wrapper");

    if (!(contentEl instanceof HTMLElement)) return;

    if (showDropdown) {
      document.body.style.overflow = "hidden";
      contentEl.style.overflow = "hidden";
      contentEl.style.height = "100vh";
    } else {
      document.body.style.overflow = "";
      contentEl.style.overflow = "";
      contentEl.style.height = "";
    }

    return () => {
      document.body.style.overflow = "";
      contentEl.style.overflow = "";
      contentEl.style.height = "";
    };
  }, [showDropdown]);

  const fetchSearch = async (query: string) => {
    try {
      const response = await fetchData({
        options: { method: "POST" },
        endpoint: HeaderFooterApiEndpoints.SEARCH_CONTENT,
        body: { query },
      });
      setSearchData(response?.data || null);
    } catch (err) {
      console.error("Search API Error:", err);
    }
  };

  const handleSearchSubmit = () => {
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    // Track search submit analytics
    const url = updateUrlParam();
    handleSearchSubmitDL(trimmed, SEC_TITLE_SEARCH, url);

    const stored = safeLocalStorageParse<string[]>("searchKeyword", []);
    const updated = [trimmed, ...stored.filter((i: string) => i !== trimmed)];
    localStorage.setItem("searchKeyword", JSON.stringify(updated));
    setSearchHistory(updated);
    redirectSearchWebForm(url, trimmed, isUserLogin);
    setSearchData(null);
    setShowDropdown(true);
  };

  const handleDelete = (e: React.MouseEvent, item: string) => {
    e.stopPropagation();

    // Get the position of the item in the search history
    const position = searchHistory.indexOf(item) + 1;

    // Track search history delete analytics
    handleSearchHistoryDeleteDL(item, false, position, SEC_TITLE_SEARCH_HISTORY);

    const updated = searchHistory.filter((i) => i !== item);
    localStorage.setItem("searchKeyword", JSON.stringify(updated));
    setSearchHistory(updated);
  };

  const handleArrowClick = (item: string) => {
    setSearchValue(item);
    setShowDropdown(true);
    fetchSearch(item);
  };

  const handleItemClick = (item: string) => {
    const isFromHistory = searchHistory.includes(item);

    const url = updateUrlParam();

    // Get index of item in history or suggestions
    let index = 0;
    if (isFromHistory) {
      index = searchHistory.indexOf(item) + 1;
    } else if (searchData?.keywords) {
      index = searchData.keywords.indexOf(item) + 1;
    }

    // Track search suggestion click analytics
    handleSearchSuggestionClickDL(item, isFromHistory, SEC_TITLE_SEARCH_SUGGESTIONS, url, index);

    const stored = safeLocalStorageParse<string[]>("searchKeyword", []);
    const updated = [item, ...stored.filter((i: string) => i !== item)];
    localStorage.setItem("searchKeyword", JSON.stringify(updated));
    setSearchHistory(updated);
    setSearchValue(item);
    setShowDropdown(false);
    redirectSearchWebForm(url, item, isUserLogin);
  };

  const normalizedSearch = searchValue.trim().toLowerCase();
  let finalList: string[] = [];

  if (!normalizedSearch) {
    finalList = searchHistory;
  } else {
    const historyMatches = searchHistory.filter((item) =>
      item.toLowerCase().includes(normalizedSearch)
    );
    const apiMatches = (searchData?.keywords || []).filter(
      (item) => !searchHistory.includes(item) && item.toLowerCase().includes(normalizedSearch)
    );
    finalList = [...historyMatches, ...apiMatches];
  }

  return (
    <>
      <span className="mx-5 my-[10px] hidden text-white/20 md:!block">|</span>
      {isMobile && !isFocused && (
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            // Track search back button click analytics
            handleSearchBackIconClickDL(searchValue, false, SEC_TITLE_BACK_ICON, 0);
            inputRef.current?.blur();
            setIsFocused(false);
            setShowDropdown(false);
            window?.BackPressed?.();
          }}
          className="flex-center-center self-center rounded px-2 text-white"
          aria-label="Back from search"
        >
          <i className="icomoon text-xl before:inline-block before:-translate-x-0.5 before:rotate-180 before:transform before:content-['\e943']" />
        </button>
      )}

      <div className="relative flex w-full flex-col gap-0 md:gap-0 md:pt-[14px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchSubmit();
          }}
          className="relative flex w-full rounded-[4px] bg-white"
        >
          <input
            type="text"
            aria-label="Search"
            ref={inputRef}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={() => setIsFocused(false)}
            className="h-11 w-full rounded-tl-[4px] px-4 py-2 text-black placeholder:text-sm focus:outline-none"
          />
          {!searchValue && (
            <span className="animate-typing pointer-events-none absolute top-[10px] right-[50px] left-4 truncate overflow-hidden pr-1 text-sm leading-[26px] whitespace-nowrap text-gray-900">
              Search Bajaj Finance
            </span>
          )}
          <button
            type="submit"
            aria-label="Submit search"
            className="flex h-[44px] w-[50px] items-center justify-center rounded-r-[4px] bg-orange-100 p-1"
          >
            <i className="icomoon text-link-primary cursor-pointer text-2xl before:content-['\e908']"></i>
          </button>
        </form>

        {showDropdown && (
          <button
            type="button"
            onClick={() => setShowDropdown(false)}
            className={`fixed right-0 bottom-0 left-0 ${version !== "in" ? "top-[75px]" : "top-[144px] md:!top-[87px]"} z-[998] bg-black/50`}
            aria-label="Close dropdown"
          />
        )}

        {showDropdown && finalList.length > 0 && (
          <div
            style={
              isMobile
                ? {
                    height: "calc(100vh - 56px)",
                    width: `${isFocused ? "calc(100% + 60px)" : "calc(100% + 32px)"}`,
                  }
                : {}
            }
            className={`absolute ${isFocused ? "left-[-44px]" : "left-[-16px]"} top-[68px] z-[999] mt-[-12px] w-full rounded-b-[8px] bg-white px-2 py-3 shadow-md md:top-full md:left-0 md:!bg-blue-100`}
          >
            <div className="rounded bg-white px-2 md:px-4">
              {finalList.map((item, index) => {
                const isFromHistory = searchHistory.includes(item);

                return (
                  <div
                    key={`${item}-${index}`}
                    className="flex cursor-pointer items-center justify-between border-b border-b-blue-700 pt-[9.2px] pb-[9.2px] md:last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      className={`flex cursor-pointer items-center gap-[14px] text-sm font-[500] ${
                        isFromHistory && normalizedSearch ? "text-[#2680eb]" : "text-[#1a1a1a]"
                      }`}
                    >
                      {isFromHistory ? (
                        <i className="icomoon text-l text-black before:content-['\e90d']"></i>
                      ) : (
                        <i className="icomoon cursor-pointer text-base before:content-['\e908']"></i>
                      )}
                      {item}
                    </button>

                    {isFromHistory ? (
                      <button
                        aria-label={`delete-${item}`}
                        onClick={(e) => handleDelete(e, item)}
                        className="cursor-pointer text-base font-[500] text-black"
                      >
                        <i className="icomoon before:content-['\e914']"></i>
                      </button>
                    ) : (
                      <button
                        aria-label={`arrow-${item}`}
                        onClick={() => handleArrowClick(item)}
                        className="rotate-225 cursor-pointer"
                      >
                        <i className="icomoon text-base text-gray-500 before:content-['\e943']"></i>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <span className="mx-5 my-[10px] hidden text-[rgba(255,255,255,0.2)] md:!block">|</span>
    </>
  );
};

export default Search;

// Helper: safely parse JSON from localStorage, returning fallback on any error
function safeLocalStorageParse<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw.trim() === "") return fallback;
    const parsed = JSON.parse(raw);
    return (parsed as T) ?? fallback;
  } catch (e) {
    try {
      localStorage.removeItem(key);
    } catch (er) {
      // ignore
    }
    return fallback;
  }
}
